import type {ReactElement} from 'react';
import React, {useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {TintAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/tint-adjustment-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';

interface PosterEditorDrawingColorPanelProps {
  panelId: string;
}

enum DrawColorOptionPanels {
  COLOR_PICKER = 'COLOR_PICKER',
  DEFAULT = 'DEFAULT',
}

export default function PosterEditorDrawingColorPanel({...props}: PosterEditorDrawingColorPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [panelToShow, setPanelToShow] = useState(DrawColorOptionPanels.DEFAULT);
  const activeItemTintColor = useGetKeyFromEffectsActiveItem('tintColor');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  if (!(activeItem?.isImage() && activeItem.isDrawing())) {
    return null;
  }

  if (!activeItemTintColor) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_color')} />;
  };

  const getContent = (): ReactElement => {
    return (
      <div className="spacing-p-t-1 spacing-p-l-4 spacing-p-r-4 spacing-p-b-4">
        <TintAdjustmentOption
          onBack={(): void => {
            setPanelToShow(DrawColorOptionPanels.COLOR_PICKER);
          }}
          onClickColorFill={(): void => {
            setPanelToShow(DrawColorOptionPanels.COLOR_PICKER);
          }}
          panelId={props.panelId}
          label={window.i18next.t('pmwjs_color')}
        />
      </div>
    );
  };

  const onChangeTintColor = (fillColor: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        effects: {
          tintColor: fillColor[0],
        },
      },
      {
        undoable,
      }
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [activeItemTintColor],
        }}
        onChangeColor={onChangeTintColor}
        onBackButton={(): void => {
          setPanelToShow(DrawColorOptionPanels.DEFAULT);
        }}
        onClose={(): void => {
          onClose();
        }}
      />
    );
  };

  const getDefaultPanel = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case DrawColorOptionPanels.COLOR_PICKER:
        return getColorPanel();
      case DrawColorOptionPanels.DEFAULT:
      default:
        return getDefaultPanel();
    }
  };

  return getPanels();
}
